import {
  Edit,
  SimpleForm,
  DateTimeInput,
  NumberInput,
  required,
  useNotify,
  useRedirect,
  TextField,
  DateField,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Box, Typography, Alert, Paper, Stack, Grid, Breadcrumbs, Link } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';

const DurationDisplay = () => {
  const durationDrivingMinutes = useWatch({ name: 'durationDrivingMinutes' }) || 0;
  const durationOnDutyMinutes = useWatch({ name: 'durationOnDutyMinutes' }) || 0;
  
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes} minutes`;
    }
    
    return remainingMinutes > 0 ? `${hours} hours ${remainingMinutes} minutes` : `${hours} hours`;
  };
  
  const totalDuration = durationDrivingMinutes + durationOnDutyMinutes;
  
  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Alert severity="info">
        <Typography variant="body2">
          <strong>Driving Time:</strong> {formatDuration(durationDrivingMinutes)}
        </Typography>
        <Typography variant="body2">
          <strong>On-Duty Time:</strong> {formatDuration(durationOnDutyMinutes)}
        </Typography>
        <Typography variant="body2">
          <strong>Total Duty Time:</strong> {formatDuration(totalDuration)}
        </Typography>
      </Alert>
    </Box>
  );
};

const EditBreadcrumbs = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const record = useRecordContext();
  
  const getDisplayName = () => {
    if (record?.startedAt) {
      const date = new Date(record.startedAt).toLocaleDateString();
      const driverName = record.user?.name || 'Unknown Driver';
      return `${driverName} - Time Log ${date}`;
    }
    return `Time Log ${id}`;
  };
  
  return (
    <Box sx={{ 
      padding: '12px 16px', 
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      marginBottom: '16px',
      border: '1px solid #e0e0e0',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    }}>
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
      >
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/driver-time-logs')}
          sx={{ 
            fontWeight: 'medium',
            cursor: 'pointer',
            '&:hover': { color: '#1976d2' } 
          }}
        >
          Driver Time Logs
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate(`/driver-time-logs/${id}/show`)}
          sx={{ 
            fontWeight: 'medium',
            cursor: 'pointer',
            '&:hover': { color: '#1976d2' } 
          }}
        >
          {getDisplayName()}
        </Link>
        <Typography color="text.primary" sx={{ fontWeight: 'medium' }}>
          Edit
        </Typography>
      </Breadcrumbs>
    </Box>
  );
};

const DriverTimeLogEditForm = () => {
  const record = useRecordContext();
  
  // Set default values with converted minutes
  const defaultValues = record ? {
    ...record,
    durationDrivingMinutes: Math.round((record.durationDriving || 0) / 60),
    durationOnDutyMinutes: Math.round((record.durationOnDuty || 0) / 60),
  } : {};

  return (
    <SimpleForm defaultValues={defaultValues}>
      <EditBreadcrumbs />
      <Typography variant="h6" gutterBottom>
        Edit Driver Time Log
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Driver
            </Typography>
            <Typography variant="h6" color="text.primary">
              <TextField source="user.name" variant="plain" />
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Originally Submitted
            </Typography>
            <Typography variant="h6" color="text.primary">
              <DateField source="submittedAt" showTime variant="plain" />
            </Typography>
          </Grid>
        </Grid>
      </Box>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DateTimeInput
            source="startedAt"
            label="Shift Start Time"
            validate={[required()]}
            fullWidth
            helperText="When did the shift start?"
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <NumberInput
            source="durationDrivingMinutes"
            label="Driving Time (minutes)"
            validate={[required()]}
            min={0}
            fullWidth
            helperText="Total time spent driving (in minutes)"
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <NumberInput
            source="durationOnDutyMinutes"
            label="On-Duty Time (minutes)"
            validate={[required()]}
            min={0}
            fullWidth
            helperText="Total time on duty including non-driving activities (in minutes)"
          />
        </Grid>
      </Grid>
      
      <DurationDisplay />
      
    </SimpleForm>
  );
};

export const DriverTimeLogEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  
  const transform = (data: any) => {
    // Convert minutes to seconds for API
    return {
      ...data,
      durationDriving: (data.durationDrivingMinutes || 0) * 60,
      durationOnDuty: (data.durationOnDutyMinutes || 0) * 60,
      // Remove the minutes fields since they're not part of the API
      durationDrivingMinutes: undefined,
      durationOnDutyMinutes: undefined,
    };
  };
  
  const onSuccess = () => {
    notify('Driver time log updated successfully');
    redirect('/driver-time-logs');
  };
  
  return (
    <Edit 
      transform={transform} 
      mutationOptions={{ onSuccess }}
    >
      <DriverTimeLogEditForm />
    </Edit>
  );
};